/**
 * List of sensitive field names to redact (case-insensitive)
 */
const SENSITIVE_FIELDS = [
  'authorization',
  'auth',
  'token',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'apikey',
  'api_key',
  'secret',
  'password',
  'passwd',
  'pwd',
  'cookie',
  'session',
  'sessionid',
  'csrf',
  'apiSecret',
  'privateKey',
  'clientSecret',
];

/**
 * Checks if a field name contains sensitive keywords
 */
function isSensitiveField(fieldName: string): boolean {
  const lowerField = fieldName.toLowerCase();
  return SENSITIVE_FIELDS.some((sensitive) => lowerField.includes(sensitive));
}

/**
 * Sanitizes an object by redacting sensitive fields
 */
function sanitizeObject(obj: Record<string, unknown>): Record<string, unknown> {
  const sanitized = { ...obj };
  Object.keys(sanitized).forEach((key) => {
    if (isSensitiveField(key)) {
      sanitized[key] = '[REDACTED]';
    }
  });
  return sanitized;
}

/**
 * Sanitizes axios config object
 */
function sanitizeAxiosConfig(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...config };

  // Sanitize headers
  if (
    'headers' in sanitized &&
    typeof sanitized.headers === 'object' &&
    sanitized.headers !== null
  ) {
    sanitized.headers = sanitizeObject(
      sanitized.headers as Record<string, unknown>,
    );
  }

  // Sanitize auth
  if ('auth' in sanitized) {
    sanitized.auth = '[REDACTED]';
  }

  // Sanitize data
  if (
    'data' in sanitized &&
    typeof sanitized.data === 'object' &&
    sanitized.data !== null
  ) {
    sanitized.data = sanitizeObject(sanitized.data as Record<string, unknown>);
  }

  // Sanitize params
  if (
    'params' in sanitized &&
    typeof sanitized.params === 'object' &&
    sanitized.params !== null
  ) {
    sanitized.params = sanitizeObject(
      sanitized.params as Record<string, unknown>,
    );
  }

  return sanitized;
}

/**
 * Sanitizes axios response object
 */
function sanitizeAxiosResponse(
  response: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...response };

  // Sanitize headers
  if (
    'headers' in sanitized &&
    typeof sanitized.headers === 'object' &&
    sanitized.headers !== null
  ) {
    sanitized.headers = sanitizeObject(
      sanitized.headers as Record<string, unknown>,
    );
  }

  // Sanitize data
  if (
    'data' in sanitized &&
    typeof sanitized.data === 'object' &&
    sanitized.data !== null
  ) {
    sanitized.data = sanitizeObject(sanitized.data as Record<string, unknown>);
  }

  return sanitized;
}

/**
 * Sanitizes error objects by removing sensitive fields before logging/reporting
 * Prevents exposure of tokens, passwords, API keys, and other sensitive data
 */
export function sanitizeError(error: unknown): unknown {
  if (!error || typeof error !== 'object') {
    return error;
  }

  // Create a shallow copy to avoid mutating the original error
  const sanitized = sanitizeObject(error as Record<string, unknown>);

  // Sanitize axios-specific structures
  if (
    'config' in sanitized &&
    typeof sanitized.config === 'object' &&
    sanitized.config !== null
  ) {
    sanitized.config = sanitizeAxiosConfig(
      sanitized.config as Record<string, unknown>,
    );
  }

  // Sanitize request object
  if (
    'request' in sanitized &&
    typeof sanitized.request === 'object' &&
    sanitized.request !== null
  ) {
    const request = { ...(sanitized.request as Record<string, unknown>) };
    if ('_header' in request) {
      request._header = '[REDACTED]';
    }
    if ('_headers' in request) {
      request._headers = '[REDACTED]';
    }
    sanitized.request = request;
  }

  // Sanitize response
  if (
    'response' in sanitized &&
    typeof sanitized.response === 'object' &&
    sanitized.response !== null
  ) {
    sanitized.response = sanitizeAxiosResponse(
      sanitized.response as Record<string, unknown>,
    );
  }

  return sanitized;
}
